// Servicio de Pedidos (Orders)
// Maneja la creación y gestión de pedidos en Firestore

import { createDocument, getDocument, getCollection, updateDocument } from './firestore.service'
import { getBusinessRules } from './businessRules.service'

const ORDERS_COLLECTION = 'orders'
const sanitizeEnvValue = (value) => typeof value === 'string' ? value.trim().replace(/^['"]|['"]$/g, '').trim() : value
const parseJsonSafe = async (resp) => {
  const text = await resp.text().catch(() => '')
  try {
    return { json: JSON.parse(text), text }
  } catch (_e) {
    return { json: null, text }
  }
}

/**
 * Generar número de pedido único
 * Formato: ORD-YYYY-XXX (ej: ORD-2024-001)
 * @returns {Promise<string>} Número de pedido único
 */
const generateOrderNumber = async () => {
  try {
    const year = new Date().getFullYear()
    const prefix = `ORD-${year}-`
    
    // Obtener todos los pedidos del año actual
    const orders = await getCollection(
      ORDERS_COLLECTION,
      [], // Sin filtros
      'createdAt',
      'desc'
    )
    
    // Filtrar pedidos del año actual y extraer números
    const currentYearOrders = orders.filter(order => {
      if (!order.orderNumber) return false
      return order.orderNumber.startsWith(prefix)
    })
    
    // Encontrar el siguiente número
    let nextNumber = 1
    if (currentYearOrders.length > 0) {
      const numbers = currentYearOrders.map(order => {
        const numStr = order.orderNumber.replace(prefix, '')
        return parseInt(numStr, 10) || 0
      })
      const maxNumber = Math.max(...numbers)
      nextNumber = maxNumber + 1
    }
    
    // Formatear con ceros a la izquierda (001, 002, etc.)
    const formattedNumber = String(nextNumber).padStart(3, '0')
    return `${prefix}${formattedNumber}`
  } catch (error) {
    console.error('Error generando número de pedido:', error)
    // Fallback: usar timestamp
    const timestamp = Date.now().toString().slice(-6)
    return `ORD-${new Date().getFullYear()}-${timestamp}`
  }
}

/**
 * Crear un nuevo pedido
 * @param {Object} orderData - Datos del pedido
 * @returns {Promise<string>} ID del pedido creado
 */
export const createOrder = async (orderData) => {
  try {
    // Generar número de pedido único
    const orderNumber = await generateOrderNumber()
    
    // Preparar datos del pedido
    const order = {
      orderNumber,
      customer: orderData.customer,
      items: orderData.items,
      subtotal: orderData.subtotal,
      shipping: orderData.shipping,
      total: orderData.total,
      payment: orderData.payment || null,
      paymentAdjustment: orderData.paymentAdjustment || null,
      fulfillment: orderData.fulfillment || null,
      status: 'pending', // Estado inicial: pendiente
      notes: orderData.notes || '',
      distance: orderData.distance || null, // Distancia en km (opcional)
      // Los timestamps (createdAt, updatedAt) se agregan automáticamente en createDocument
    }
    
    // Crear el pedido en Firestore
    const orderId = await createDocument(ORDERS_COLLECTION, order)
    
    // Actualizar datos del cliente en la colección customers
    try {
      const { updateCustomerFromOrder } = await import('./customers.service')
      await updateCustomerFromOrder(orderData.customer)
    } catch (customerError) {
      // No interrumpir la creación del pedido si falla la actualización del cliente
      console.warn('Error actualizando datos del cliente:', customerError)
    }
    
    return { orderId, orderNumber }
  } catch (error) {
    console.error('Error creando pedido:', error)
    throw error
  }
}

export const createCheckoutOrder = async ({ orderData, storefrontUrl, trace, traceId }) => {
  const baseUrl = sanitizeEnvValue(import.meta.env.VITE_API_BASE_URL) || ''
  const url = `${baseUrl}/api/checkout/create`
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ orderData, storefrontUrl, trace, traceId })
  })
  const { json, text } = await parseJsonSafe(resp)
  if (!resp.ok) {
    const details = json?.details ? `: ${json.details}` : ''
    const stage = json?.stage ? ` [${json.stage}]` : ''
    const requestId = json?.requestId ? ` (${json.requestId})` : ''
    const message = json?.error ? `${json.error}${details}${stage}${requestId}` : (text?.slice(0, 160) || `Error Checkout: ${resp.status}`)
    const error = new Error(message)
    error.status = resp.status
    error.data = json || null
    throw error
  }
  return json
}

/**
 * Obtener un pedido por ID
 * @param {string} orderId - ID del pedido
 * @returns {Promise<Object|null>} Pedido o null si no existe
 */
export const getOrder = async (orderId) => {
  try {
    return await getDocument(ORDERS_COLLECTION, orderId)
  } catch (error) {
    console.error('Error obteniendo pedido:', error)
    throw error
  }
}

/**
 * Obtener pedidos por email del cliente
 * @param {string} email - Email del cliente
 * @returns {Promise<Array>} Array de pedidos
 */
export const getOrdersByEmail = async (email) => {
  try {
    const orders = await getCollection(
      ORDERS_COLLECTION,
      [{ field: 'customer.email', operator: '==', value: email }],
      'createdAt',
      'desc'
    )
    return orders
  } catch (error) {
    console.error('Error obteniendo pedidos por email:', error)
    throw error
  }
}

/**
 * Obtener todos los pedidos (para admin)
 * @param {Object} options - Opciones de consulta
 * @returns {Promise<Array>} Array de pedidos
 */
export const getAllOrders = async (options = {}) => {
  try {
    const {
      status = null,
      orderByField = 'createdAt',
      orderDirection = 'desc',
      limitResults = null
    } = options

    const filters = []
    
    if (status) {
      filters.push({ field: 'status', operator: '==', value: status })
    }
    
    return await getCollection(
      ORDERS_COLLECTION,
      filters,
      orderByField,
      orderDirection,
      limitResults
    )
  } catch (error) {
    console.error('Error obteniendo pedidos:', error)
    throw error
  }
}

/**
 * Actualizar el estado de un pedido
 * @param {string} orderId - ID del pedido
 * @param {string} status - Nuevo estado
 * @returns {Promise<void>}
 */
export const updateOrderStatus = async (orderId, status) => {
  try {
    await updateDocument(ORDERS_COLLECTION, orderId, { status })
    
    // Actualizar el estado de la venta asociada
    try {
      const rules = await getBusinessRules()
      const mode = rules?.ordersSalesMode?.mode || 'linked_dependent'
      if (mode === 'linked_independent') return
      const { getSaleByOrderId, updateSale } = await import('./sales.service')
      const sale = await getSaleByOrderId(orderId)
      
      if (sale) {
        if (mode === 'sales_only') {
          if (status === 'cancelled') {
            await updateSale(sale.id, { status: 'cancelled' })
          }
          return
        }
        // Si el pedido está entregado y la venta está cobrada, marcar como completada
        if (status === 'delivered' && sale.paid) {
          await updateSale(sale.id, { status: 'completed' })
        } 
        // Si el pedido está entregado pero no está cobrada, mantener como pendiente
        else if (status === 'delivered' && !sale.paid) {
          await updateSale(sale.id, { status: 'pending_payment' })
        }
        // Si el pedido está cancelado, cancelar la venta
        else if (status === 'cancelled') {
          await updateSale(sale.id, { status: 'cancelled' })
        }
        // Para otros estados, mantener el estado actual de la venta
      }
    } catch (saleError) {
      // No interrumpir la actualización del pedido si falla la actualización de la venta
      console.warn('Error actualizando estado de la venta:', saleError)
    }
  } catch (error) {
    console.error('Error actualizando estado del pedido:', error)
    throw error
  }
}

/**
 * Actualizar un pedido
 * @param {string} orderId - ID del pedido
 * @param {Object} updates - Datos a actualizar
 * @returns {Promise<void>}
 */
export const updateOrder = async (orderId, updates) => {
  try {
    await updateDocument(ORDERS_COLLECTION, orderId, updates)
  } catch (error) {
    console.error('Error actualizando pedido:', error)
    throw error
  }
}

export default {
  createOrder,
  getOrder,
  getOrdersByEmail,
  getAllOrders,
  updateOrderStatus,
  updateOrder
}

